import projectValidation from './projectValidation';
import { useKernelApi } from 'infrastructure/api';
import { ResourceData, ResourceId, useCrudService } from 'framework/crud';
import { cloneDeep } from 'lodash';
import useProjectsSettings from './useProjectsSettings';

const useProjectService = () => {
  const { areArchivedProjectsVisible } = useProjectsSettings();
  const singleResourceUrl = '/projects/:id';
  const {
    search: baseSearch,
    update: initialUpdate,
    ...baseCrudRequests
  } = useCrudService({
    singleResourceUrl,
    listResourceUrl: '/projects',
    useService: useKernelApi,
  });

  // This is filtered by (sowId != null && accountId != null)
  const { searchAll: searchAllSuggestions } = useCrudService({
    singleResourceUrl,
    listResourceUrl: '/projects/suggestions',
    useService: useKernelApi,
  });

  // This returns all possible suggestions, not additional filters
  const { search: searchSuggestions } = useCrudService({
    singleResourceUrl,
    listResourceUrl: '/projects/suggestions-all',
    useService: useKernelApi,
  });

  const { sendPostRequest } = useKernelApi();

  const search = (request: ResourceData) => {
    const searchParams = cloneDeep(request);
    const searchString = searchParams.filter?.['specification:search']?.eq || '';

    if (searchString && searchParams.filter) {
      delete searchParams.filter?.['specification:search'];
      searchParams.filter['specification:realNameOrCodeName'] = {
        ct: searchString,
      };
    }

    if (searchParams.filter && searchParams.filter?.name) {
      searchParams.filter['specification:realNameOrCodeName'] = searchParams.filter.name;
      delete searchParams.filter?.name;
    }

    searchParams.filter.archived = {
      eq: areArchivedProjectsVisible,
    };

    return baseSearch(searchParams);
  };

  const getValidationSchema = () => {
    return Promise.resolve(projectValidation);
  };

  const mapProject = ({ availableBudgets, rbsTreeNodeName, rbsTreeNodeId, ...project }: any) => {
    return {
      ...project,
      rbsTreeNode: {
        id: rbsTreeNodeId,
      },
      availableBudgetsIds: availableBudgets ? availableBudgets.map((budget: any) => budget.value) : [],
      sow: {
        id: project?.sow?.id,
      },
    };
  };

  const createProject = async (project: any) => {
    const response = await sendPostRequest('/projects', mapProject(project));
    return response.json();
  };

  const update = async (id: ResourceId, project: any) => {
    return initialUpdate(id, mapProject(project));
  };

  const getName = async (id: ResourceId) => {
    const response = await sendPostRequest(`/projects/${id}/name/sensitive`);
    return response.json();
  };

  const archiveObjects = async (projectId: ResourceId, budgetId?: ResourceId) => {
    return sendPostRequest(`/archive/objects`, {
      projectId,
      budgetId,
    });
  };

  return {
    getValidationSchema,
    createProject,
    ...baseCrudRequests,
    search,
    update,
    getName,
    archiveObjects,
    searchSuggestions,
    searchAllSuggestions,
  };
};

export default useProjectService;
